// Copyright ©️ 2026 eVolve MEP, LLC

import './mantine-styles.css';

import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { AllEnterpriseModule, IntegratedChartsModule, LicenseManager, ModuleRegistry } from 'ag-grid-enterprise';

import { EvolveApiProvider } from 'app/EvolveApiProvider';
import { AutodeskAuthProvider } from 'components/Autodesk/auth/useAutodeskAuth';
import { FilesCacheProvider } from 'components/Mantine/FileUploads/useFileCache';
import { ModalStackProvider } from 'components/Mantine/Modals/useModalStack';
import { AppNeedsRefreshProvider } from 'components/Mantine/Navigation/useAppNeedsRefresh';
import { MantineNotificationsProvider } from 'components/Mantine/useMantineNotifications';
import { ExportGridProvider } from 'helpers/ag-grid/Exports/useExportGrid';
import { BlockerProvider } from 'hooks/navigation/useBlockerContext';
import { EvolveGetWithLocalStorageProvider } from 'hooks/useEvolveGetWithLocalStorage';
import { LocalStorageProvider } from 'hooks/useLocalStorage';
import { MaybeUserProvider } from 'hooks/user/useMaybeUser';
import { UsersInfoProvider } from 'hooks/user/useUsersInfo';
import { useGenerateMantineTheme } from 'styles/theme/useGenerateMantineTheme';
import { variableResolver } from 'styles/theme/variableResolver';

import { AppRoutes } from './AppRoutes';
import { ErrorBoundary } from './ErrorBoundary';
import { MessagesProvider } from './MessageCenter/MessagesContext';

ModuleRegistry.registerModules([AllEnterpriseModule, IntegratedChartsModule.with(AgChartsEnterpriseModule)]);
const agGridLicenseKey =
  'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-127496}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Evolve_MEP}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Evolve_Shop}_only_for_{7}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Evolve_Shop}_need_to_be_licensed___{Evolve_Shop}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{21_July_2027}____[v3]_[0102]_MTgxNjEyNDQwMDAwMA==915caa31da18a77077ac3bf6ea634054' as const;
LicenseManager.setLicenseKey(agGridLicenseKey);

export const App = () => {
  const theme = useGenerateMantineTheme(window.location.hostname !== 'foresite.evolvemep.com');
  return (
    <MantineProvider defaultColorScheme="light" withCssVariables theme={theme} cssVariablesResolver={variableResolver}>
      <ModalStackProvider>
        <LocalStorageProvider>
          <EvolveGetWithLocalStorageProvider>
            <BlockerProvider>
              <AppNeedsRefreshProvider>
                <ErrorBoundary>
                  <MantineNotificationsProvider>
                    <EvolveApiProvider>
                      <MaybeUserProvider>
                        <FilesCacheProvider>
                          <UsersInfoProvider>
                            <ExportGridProvider>
                              <DatesProvider
                                settings={{
                                  firstDayOfWeek: 0,
                                  weekendDays: [],
                                }}
                              >
                                <MessagesProvider>
                                  <AutodeskAuthProvider>
                                    <AppRoutes />
                                  </AutodeskAuthProvider>
                                </MessagesProvider>
                              </DatesProvider>
                            </ExportGridProvider>
                          </UsersInfoProvider>
                        </FilesCacheProvider>
                      </MaybeUserProvider>
                    </EvolveApiProvider>
                  </MantineNotificationsProvider>
                </ErrorBoundary>
              </AppNeedsRefreshProvider>
            </BlockerProvider>
          </EvolveGetWithLocalStorageProvider>
        </LocalStorageProvider>
      </ModalStackProvider>
    </MantineProvider>
  );
};
